import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatISK } from '@/utils/formatISK';
import { Loader2, X, Plus, Trash2, Send, Save, FileText, AlertTriangle, Calendar, CheckCircle2, ChevronDown, ChevronRight, Pencil, RotateCcw, Copy, Zap } from 'lucide-react';
import PortalOverlay from '@/components/PortalOverlay';
import { Checkbox } from '@/components/ui/checkbox';
import { tenantInvoicesAPI, companyAPI, projectsAPI } from '@/services/api';
import { toast } from 'sonner';
import { useIntegrationsAccess } from '@/hooks/useIntegrationsAccess';
import { formatKlst, formatHoursNum } from '@/utils/formatHours';
import { confirmDialog } from '@/components/ConfirmDialog';
import DateFieldIS from '@/components/DateFieldIS';
import MoneyInput from '@/components/MoneyInput';
import { useAuth } from '@/contexts/AuthContext';

const ISK = (n) => formatISK(Math.round(Number(n) || 0));
const VAT_OPTIONS = [
  { value: 24, label: '24% (almennur)' },
  { value: 11, label: '11% (matur, ferðaþj.)' },
  { value: 0, label: '0% / utan VSK' },
];

const AGGREGATED_KINDS = new Set(['hours', 'aukavinna', 'material', 'receipt']);
const isAggregated = (kind) => AGGREGATED_KINDS.has((kind || '').toLowerCase());

/**
 * Filter the `sources` payload (from GET /billable-sources) to just the
 * rows compatible with a given line kind. Rate-mismatched hours entries
 * (i.e. an entry logged at a different hourly_rate than this line's
 * unit_price) are excluded — those will surface on their own aggregated
 * line elsewhere and cannot be ticked onto this one.
 */
/**
 * iter382: rows a given line may claim.
 *
 * A receipt line is ONE receipt, but the pool here used to be every receipt on
 * the project, so any re-pick collapsed all receipt lines onto whichever
 * receipts were still available. On Frans - Bústaður that left three BYKO
 * lines all carrying the 24.7 receipt: the reconciliation counted it once and
 * the banner counted all three amounts, 9.587 kr of drift.
 *
 * A row already claimed by ANOTHER line is excluded, so two lines can never
 * end up with the same source_id.
 */
const sourcesForLine = (sources, line, allLines) => {
  if (!sources) return [];
  const claimedByOthers = new Set();
  (allLines || []).forEach((l) => {
    if (l.id === line.id) return;
    (l.source_ids || []).forEach((sid) => claimedByOthers.add(sid));
  });
  const mine = new Set(line.source_ids || []);
  const free = (rows) => rows.filter((r) => mine.has(r.id) || !claimedByOthers.has(r.id));
  const kind = (line.kind || '').toLowerCase();
  const lineRate = Math.round(Number(line.unit_price) || 0);
  if (kind === 'hours') {
    return free((sources.time_entries || []).filter((te) =>
      !te.is_extra_work
      && Number(te.extra_work_hours || 0) === 0
      && Math.round(Number(te.hourly_rate) || 0) === lineRate,
    ));
  }
  if (kind === 'aukavinna') {
    return free((sources.time_entries || []).filter((te) =>
      te.is_extra_work || Number(te.extra_work_hours || 0) > 0,
    ));
  }
  if (kind === 'material') return free(sources.materials || []);
  if (kind === 'receipt') return free(sources.receipts || []);
  return [];
};

/**
 * Recompute a line's `quantity` and `unit_price` from a set of currently
 * ticked source_ids. Rules:
 *   - hours line: quantity = Σ hours over ticked regular entries.
 *                 unit_price stays fixed at the line's per-hour rate.
 *   - aukavinna:  quantity = Σ (is_extra_work ? hours : extra_work_hours)
 *                 over ticked entries. unit_price stays fixed.
 *   - material:   quantity = 1; unit_price = Σ net_amount over ticked rows.
 *   - receipt:    quantity = 1; unit_price fixed (each receipt is 1 line).
 *   - custom:     untouched (user edits manually).
 */
const deriveLineTotals = (line, tickedSet, sources) => {
  const kind = (line.kind || '').toLowerCase();
  if (kind === 'hours') {
    const rows = (sources?.time_entries || []).filter((te) => tickedSet.has(te.id) && !te.is_extra_work && Number(te.extra_work_hours || 0) === 0);
    const hours = rows.reduce((s, te) => s + Number(te.hours || 0), 0);
    return { quantity: Number(hours.toFixed(2)), unit_price: line.unit_price };
  }
  if (kind === 'aukavinna') {
    const rows = (sources?.time_entries || []).filter((te) => tickedSet.has(te.id) && (te.is_extra_work || Number(te.extra_work_hours || 0) > 0));
    const hours = rows.reduce((s, te) => s + Number(te.is_extra_work ? te.hours : te.extra_work_hours || 0), 0);
    return { quantity: Number(hours.toFixed(2)), unit_price: line.unit_price };
  }
  if (kind === 'material') {
    const rows = (sources?.materials || []).filter((m) => tickedSet.has(m.id));
    const net = rows.reduce((s, m) => s + Number(m.net_amount || 0), 0);
    return { quantity: 1, unit_price: Math.round(net) };
  }
  if (kind === 'receipt') {
    // iter385 #1: the same `invoice_amount` the server will put on the line,
    // so the modal can never show a receipt line the save would change.
    const rows = (sources?.receipts || []).filter((r) => tickedSet.has(r.id));
    const amount = rows.reduce(
      (s, r) => s + Number(r.invoice_amount ?? r.net_amount ?? 0), 0);
    return { quantity: 1, unit_price: Math.round(amount) };
  }
  return { quantity: line.quantity, unit_price: line.unit_price };
};

/**
 * iter379: a native <input type="date"> renders in the browser's own locale,
 * which showed "22 Oct 2026" to an Icelandic contractor. The field now lives
 * in `components/DateFieldIS.js` so every screen uses the same one
 * (Phase 1 #9).
 */
const formatShortDate = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${parseInt(d, 10)}.${parseInt(m, 10)}.`;
};

// NOTE: `computeLineTotals` used to live here (client-side per-line
// net/vat/total math). It was removed as part of Phase 4 follow-up
// (reviewer round #4): the frontend must NOT hold its own copy of
// money math. `Math.round(0.5)=1` (JS) vs `round(0.5)=0` (Python
// banker's) disagree on ~2.3% of ordinary inputs, producing a subtotal
// in the modal that could differ by 1 kr from the saved invoice.
// The modal now displays server-computed values from
// `POST /api/invoices/preview-totals`, which routes through the SAME
// `tenant_invoices.compute_invoice_totals` the write path uses.

const emptyLine = () => ({
  id: `local_${Math.random().toString(36).slice(2, 10)}`,
  kind: 'custom',
  description: '',
  quantity: 1,
  unit: 'stk',
  unit_price: 0,
  vat_rate: 24,
});

/**
 * InvoiceEditorModal — full-screen on mobile, centered card on desktop.
 *
 * Two entry modes:
 *  1. New invoice from a project → loads /projects/:id/invoice/draft to prefill
 *  2. Edit existing draft → loads /invoices/:id and lets the user save changes
 *
 * Always shows: customer block, line items table, dates, notes, totals, action buttons.
 * "Senda reikning" pushes to Konto (if connected) — otherwise marks as sent_manual locally.
 */
const InvoiceEditorModal = ({ open, onClose, projectId, invoiceId, onSent,
  preSelectedEntryIds, preSelectedReceiptIds, preSelectedMaterialIds }) => {
  const { allowed: integrationsAllowed } = useIntegrationsAccess();
  // Terminology switches based on whether this tenant has Konto/Payday
  // push access. Non-allowlisted users see "reikningsfærsla" language
  // (we ONLY track marks — no real sending). Reynir dogfood account keeps
  // the full "reikningur"/"senda" language because the Konto path is live.
  const T = integrationsAllowed
    ? {
        titleNew: 'Gera reikning',
        titleEdit: 'Breyta reikningi',
        subtitle: 'Drögin eru ekki send fyrr en þú smellir á „Senda reikning".',
        manualConfirm: 'Þetta merkir vinnuna sem reikningsfærða INNANHÚSS, enginn reikningur er sendur í Konto. Halda áfram?',
        manualBtnTitle: 'Merkir tíma og kvittanir sem reikningsfært, engin Konto sending',
        toastManual: 'Reikningur merktur sem reikningsfærður',
      }
    : {
        titleNew: 'Gera reikning',
        titleEdit: 'Breyta reikningsfærslu',
        subtitle: 'Búðu til samantekt yfir vinnu og efni sem þú getur svo notað til að búa til reikning í bókhaldskerfinu þínu.',
        manualConfirm: 'Ertu viss? Þetta merkir vinnuna og efnið sem reikningsfært í Rektra. Ekki er hægt að taka það til baka nema með því að opna færsluna aftur.',
        manualBtnTitle: 'Merkir vinnu og efni sem reikningsfært í Rektra',
        toastManual: 'Merkt sem reikningsfært',
      };
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  // iter390: one key per open modal, so a double click, a retry or a second
  // tab can never turn one invoice into two.
  const idempotencyKeyRef = useRef(
    (window.crypto?.randomUUID?.() || `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`)
  );
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);   // server-shape draft data
  const [sources, setSources] = useState(null); // GET /billable-sources output — powers the per-line checklist
  const vatLocked = draft?.vat_registered === false;
  // iter380: a company that has never answered the VAT question is asked once,
  // here, on its first invoice draft. Until it answers it counts as registered.
  const [vatAnswerSaving, setVatAnswerSaving] = useState(false);
  const [vatAsked, setVatAsked] = useState(false);
  const askVatRegistered = draft?.ask_vat_registered === true && !vatAsked;

  const answerVatRegistered = async (isRegistered) => {
    setVatAnswerSaving(true);
    try {
      await companyAPI.updateSettings({ is_vat_registered: isRegistered });
      setVatAsked(true);
      toast.success(isRegistered
        ? 'Skráð: fyrirtækið er á VSK-skrá'
        : 'Skráð: fyrirtækið er ekki á VSK-skrá');
      setDraft((d) => (d ? { ...d, ask_vat_registered: false, vat_registered: isRegistered } : d));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tókst ekki að vista svarið');
    } finally {
      setVatAnswerSaving(false);
    }
  };
  const [expandedLines, setExpandedLines] = useState({}); // { [lineId]: true } — controls the row-picker visibility per line
  // Per-line "manual override" state. Aggregated line quantity + material
  // unit_price are read-only by default (derived from ticked checkboxes).
  // Tapping "Breyta upphæð" flips the line here and unlocks manual entry.
  // While unlocked: toggling a checkbox still updates source_ids but does
  // NOT overwrite the manually-entered numbers. Re-locking recomputes them
  // from the current tick set.
  const [manualLines, setManualLines] = useState({}); // { [lineId]: true }
  // Quick-select bar state (Phase 2). Only the mode is persisted between
  // renders — the transient inputs (from/to dates, target amount) live
  // here too so the picker sub-panels are controlled.
  const [qsMode, setQsMode] = useState(null);       // null | 'timabil' | 'upphaed'
  const [qsFromDate, setQsFromDate] = useState(''); // ISO YYYY-MM-DD
  const [qsToDate, setQsToDate] = useState('');
  const [qsAmount, setQsAmount] = useState('');     // ISK, net
  // Whether the initial default "Allt" pre-tick has already been applied
  // this modal-open. Guarded so re-fetching billable-sources on remount
  // doesn't re-tick rows the operator has deliberately unticked.
  const initialAllAppliedRef = useRef(false);
  const [summary, setSummary] = useState(null);
  const [persistedId, setPersistedId] = useState(invoiceId || null);
  const [error, setError] = useState(null);
  // iter372: the 409 "Tölurnar á þessu verki stemma ekki" means nothing was
  // written and nothing may be written. Both write buttons go dead until the
  // verk is fixed, so the operator cannot keep pressing them.
  const [invariantBlocked, setInvariantBlocked] = useState(false);
  // iter353: set when a selection-scoped draft could not be built, or came
  // back covering different rows than the operator ticked. While it is set
  // there is NO draft on screen and nothing can be marked billed.
  const [selectionError, setSelectionError] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  // Iter309: collapsible "Fleiri aðgerðir" section housing the secondary
  // export helpers (PDF, CSV). Copy summary was promoted to the primary
  // footer as a first-class action alongside Send / Merkja handvirkt.
  const [moreOpen, setMoreOpen] = useState(false);
  // "Ítarleg upplýsingar" toggle for the customer address / zip / city fields.
  // Starts collapsed for new drafts; auto-expands when the draft already
  // has any of those fields populated (see the render block for the fallback).
  const [showAdvancedCustomer, setShowAdvancedCustomer] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  // Mirror of draft for use inside loadDraft so the callback identity stays
  // stable across edits (otherwise useEffect re-fires on every keystroke).
  const draftRef = useRef(null);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // iter353: the rows the operator ticked on the Skýrsla. This list is sent
  // WITH the draft request, so the server builds the draft from exactly
  // these rows. The phone no longer trims anything after the fact: a slow
  // or dead second request used to leave the full-project draft on screen.
  const selection = useMemo(() => {
    const entryIds = Array.isArray(preSelectedEntryIds) ? preSelectedEntryIds : [];
    const receiptIds = Array.isArray(preSelectedReceiptIds) ? preSelectedReceiptIds : [];
    const materialIds = Array.isArray(preSelectedMaterialIds) ? preSelectedMaterialIds : [];
    if (entryIds.length === 0 && receiptIds.length === 0 && materialIds.length === 0) return null;
    return {
      scope: 'extra_work',
      entry_ids: entryIds,
      receipt_ids: receiptIds,
      material_ids: materialIds,
    };
  }, [preSelectedEntryIds, preSelectedReceiptIds, preSelectedMaterialIds]);
  const selectionIdSet = useMemo(() => new Set(
    selection ? [...selection.entry_ids, ...selection.receipt_ids, ...selection.material_ids] : [],
  ), [selection]);

  const loadDraft = useCallback(async ({ scoped = false } = {}) => {
    if (scoped) setReloading(true); else setLoading(true);
    setError(null);
    try {
      let data;
      if (invoiceId && !scoped) {
        const r = await tenantInvoicesAPI.get(invoiceId);
        data = r.data;
        setPersistedId(invoiceId);
      } else if (projectId) {
        const body = {};
        if (selection) {
          // Selection mode: the ids ARE the scope. No date guessing.
          body.selection = selection;
        } else {
          if (rangeFrom) body.from_date = rangeFrom;
          if (rangeTo) body.to_date = rangeTo;
        }
        const r = await tenantInvoicesAPI.draftFromProject(projectId, body);
        data = r.data;
        data.project_id = projectId;
        if (selection) {
          const got = new Set();
          (data.lines || []).forEach((l) => {
            (l.source_ids || []).forEach((sid) => got.add(sid));
            if (l.source_id) got.add(l.source_id);
          });
          const sameSize = got.size === selectionIdSet.size;
          const sameRows = sameSize && [...selectionIdSet].every((sid) => got.has(sid));
          if (!sameRows) {
            setDraft(null);
            setSummary(null);
            setSelectionError(
              'Reikningsfærslan passar ekki við það sem þú valdir. '
              + 'Lokaðu glugganum, endurnýjaðu skýrsluna og veldu aftur.',
            );
            return;
          }
        }
      }
      const norm = (d) => (typeof d === 'string' ? d.slice(0, 10) : '');
      const prev = draftRef.current;
      // Keep user-edited issue/due dates + customer/notes when reloading lines mid-edit.
      if (scoped && prev) {
        data.issue_date = prev.issue_date;
        data.due_date = prev.due_date;
        data.final_due_date = prev.final_due_date;
        data.customer = prev.customer || data.customer;
        data.notes_public = prev.notes_public || data.notes_public;
        data.notes_internal = prev.notes_internal || data.notes_internal;
      } else {
        data.issue_date = norm(data.issue_date) || new Date().toISOString().slice(0, 10);
        data.due_date = norm(data.due_date) || '';
        data.final_due_date = norm(data.final_due_date) || '';
      }
      data.lines = (data.lines || []).map((l) => ({
        ...l,
        source_ids: Array.isArray(l.source_ids) ? l.source_ids : (l.source_id ? [l.source_id] : []),
      }));
      if (!data.customer) data.customer = {};
      setSummary(data.summary || null);
      setDraft(data);

      // iter326: Backend flags aukavinna lines that have qty but no rate
      // (project has no `extra_work_hourly_rate` set) as needs_rate=true.
      // Auto-unlock manual entry so the operator can type the rate straight
      // into the line without hunting for the "Breyta upphæð" button.
      const needsRateLines = (data.lines || []).filter((l) => l.needs_rate);
      if (needsRateLines.length > 0) {
        setManualLines((m) => {
          const next = { ...m };
          needsRateLines.forEach((l) => { next[l.id] = true; });
          return next;
        });
      }

      // Fetch the per-row source detail so the checklist under each
      // aggregated line can render (dates, hours, amount per row).
      const pid = data.project_id || projectId;
      if (pid) {
        try {
          const sr = await tenantInvoicesAPI.billableSources(pid, invoiceId || null);
          setSources(sr.data);
        } catch (e) {
          // Non-fatal: fall back to no checklist. The user can still edit
          // custom lines and see aggregated totals.
          setSources({ time_entries: [], receipts: [], materials: [] });
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Ekki tókst að hlaða reikningsfærsluna';
      if (selection) {
        // NEVER fall back to a full-project draft: billing everything is a
        // worse outcome than billing nothing.
        setDraft(null);
        setSummary(null);
        setSelectionError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [projectId, invoiceId, rangeFrom, rangeTo, selection, selectionIdSet]);

  useEffect(() => {
    if (open) loadDraft({ scoped: false });
    else {
      setDraft(null); setConfirmSend(false); setError(null); setSelectionError(null);
      setPersistedId(invoiceId || null);
      setRangeFrom(''); setRangeTo(''); setSummary(null); setSources(null); setExpandedLines({}); setManualLines({});
      setQsMode(null); setQsFromDate(''); setQsToDate(''); setQsAmount('');
      initialAllAppliedRef.current = false;
    }
  }, [open, projectId, invoiceId]);

  // Phase 2 "Allt" default behaviour: for a brand-new draft (no invoiceId
  // prop), once billable-sources has loaded, silently strip any pre-ticked
  // rows whose date is AFTER the invoice's issue_date. Contractors would
  // never intentionally bill future work — but backend prefill_from_project
  // returns every unbilled row regardless of date, so the trim happens
  // client-side. Fires once per modal-open, guarded by
  // initialAllAppliedRef. Skipped for existing-invoice edits so we never
  // silently mutate a saved draft the user is re-opening.
  useEffect(() => {
    if (invoiceId) return;                    // editing existing → leave alone
    // iter353: a selection-scoped draft arrives correct from the server and is
    // never touched here. This effect only serves full-project drafts.
    if (selection) return;
    if (initialAllAppliedRef.current) return; // already applied this session
    if (!draft || !sources) return;
    initialAllAppliedRef.current = true;
    const issueDate = draft.issue_date || new Date().toISOString().slice(0, 10);
    const nextLines = draft.lines.map((line) => {
      if (!isAggregated(line.kind)) return line;
      const available = sourcesForLine(sources, line, draft.lines);
      const dateById = new Map(available.map((r) => [r.id, r.date || '']));
      const kept = (line.source_ids || []).filter((sid) => {
        const d = dateById.get(sid);
        if (!d) return true;
        return d <= issueDate;
      });
      if (kept.length === (line.source_ids || []).length) return line;
      const derived = deriveLineTotals(line, new Set(kept), sources);
      return { ...line, source_ids: kept, ...derived };
    });
    const originalLen = (draft.lines || []).length;
    const changed = nextLines.length !== originalLen
      || nextLines.some((l, i) => l !== draft.lines[i]);
    if (changed) {
      setDraft((d) => ({ ...d, lines: nextLines }));
    }
  }, [draft, sources, invoiceId, selection]);

  const setLine = (idx, patch) => {
    setDraft((d) => {
      const lines = d.lines.slice();
      lines[idx] = { ...lines[idx], ...patch };
      return { ...d, lines };
    });
  };
  const addLine = () => setDraft((d) => ({ ...d, lines: [...(d.lines || []), emptyLine()] }));
  const removeLine = (idx) => setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));
  const setCustomer = (field, val) => setDraft((d) => ({ ...d, customer: { ...(d.customer || {}), [field]: val } }));

  /**
   * Toggle whether a specific underlying row (time_entry / receipt /
   * material) is included in an aggregated line. Recomputes the line's
   * quantity + unit_price from the resulting source_ids set — UNLESS the
   * line is in manual-override mode (`manualLines[lineId] === true`), in
   * which case source_ids updates but the operator's typed quantity/price
   * stay untouched. If the last tick is removed from an aggregated line,
   * the line itself is removed (no more zero-quantity rows lingering to
   * fail validation).
   */
  const toggleSource = (lineIdx, sourceId) => {
    setDraft((d) => {
      const line = d.lines[lineIdx];
      if (!line) return d;
      const currentSet = new Set(line.source_ids || []);
      if (currentSet.has(sourceId)) currentSet.delete(sourceId);
      else currentSet.add(sourceId);
      const newIds = Array.from(currentSet);
      if (isAggregated(line.kind) && newIds.length === 0) {
        toast.info('Lína fjarlægð, engar færslur valdar');
        // Also drop any manual-override flag for this line.
        setManualLines((m) => {
          const { [line.id]: _drop, ...rest } = m;
          return rest;
        });
        return { ...d, lines: d.lines.filter((_, i) => i !== lineIdx) };
      }
      const isManual = !!manualLines[line.id];
      const derived = isManual
        ? { quantity: line.quantity, unit_price: line.unit_price }
        : deriveLineTotals(line, currentSet, sources);
      const lines = d.lines.slice();
      lines[lineIdx] = { ...line, source_ids: newIds, ...derived };
      return { ...d, lines };
    });
  };

  const toggleLineExpansion = (lineId) => {
    setExpandedLines((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  /**
   * Flip an aggregated line into manual-override mode. Once unlocked the
   * operator can type any quantity / unit_price (e.g. round 79.83 → 80,
   * give a discount, invoice a flat amount). Checkbox toggles still
   * change what's marked settled, but no longer mutate the typed number.
   * Locking again recomputes both from the current tick set.
   */
  const unlockLineManualEntry = (lineIdx) => {
    setDraft((d) => {
      const line = d.lines[lineIdx];
      if (!line) return d;
      setManualLines((m) => ({ ...m, [line.id]: true }));
      // Make sure the checklist is visible so the operator sees the note.
      setExpandedLines((prev) => ({ ...prev, [line.id]: true }));
      return d;
    });
  };
  const relockLineManualEntry = (lineIdx) => {
    setDraft((d) => {
      const line = d.lines[lineIdx];
      if (!line) return d;
      const tickedSet = new Set(line.source_ids || []);
      const derived = deriveLineTotals(line, tickedSet, sources);
      const lines = d.lines.slice();
      lines[lineIdx] = { ...line, ...derived };
      setManualLines((m) => {
        const { [line.id]: _drop, ...rest } = m;
        return rest;
      });
      return { ...d, lines };
    });
  };

  /**
   * Phase 1 #2: the aukavinna line arrives at 0 kr when the verkefni has no
   * `extra_work_hourly_rate` (every project created from an accepted quote
   * is in that state). Saving was then blocked by "Einingaverð má ekki vera
   * 0" with no hint where the rate lives. The operator now types the rate
   * right here and it is saved on the verkefni, so the next invoice is
   * already right.
   */
  const [extraRateInput, setExtraRateInput] = useState('');
  const [savingExtraRate, setSavingExtraRate] = useState(false);
  const { user } = useAuth();
  // Phase 1 review #4: saving a rate ON THE VERKEFNI is an owner/admin call.
  // Anyone else still types the amount straight onto the line, so it is never
  // a dead end for them either.
  const canSetProjectRate = ['owner', 'admin'].includes(user?.role);
  const saveExtraWorkRate = async (lineIdx) => {
    const rate = Number(extraRateInput) || 0;
    if (rate <= 0) {
      toast.error('Sláðu inn taxta hærri en 0 kr');
      return;
    }
    const pid = draftRef.current?.project_id || projectId;
    setSavingExtraRate(true);
    try {
      if (pid) await projectsAPI.update(pid, { extra_work_hourly_rate: rate });
      setDraft((d) => {
        if (!d) return d;
        const lines = d.lines.slice();
        const line = lines[lineIdx];
        if (!line) return d;
        lines[lineIdx] = { ...line, unit_price: rate, needs_rate: false };
        return { ...d, lines };
      });
      setExtraRateInput('');
      toast.success(`Aukavinnutaxti ${ISK(rate)} kr/klst vistaður á verkefnið`);
    } catch {
      toast.error('Ekki tókst að vista taxtann á verkefnið. Þú getur samt slegið upphæðina inn á línuna.');
    } finally {
      setSavingExtraRate(false);
    }
  };

  /**
   * Compute the net amount a row contributes when ticked. Used by "Upphæð"
   * to walk rows in date order until the cumulative net meets the target.
   */
  const rowNet = (row) => {
    if (row == null) return 0;
    // iter385 #1: `invoice_amount` is the server's own valuation — the exact
    // number the invoice line will carry (gross for a company outside the VAT
    // register, net on it). The picker used `net_amount` and therefore
    // measured 17.481 where the line charged 21.676, so "Upphæð 276.000"
    // produced a 277.676 kr draft.
    if (row.invoice_amount != null) return Number(row.invoice_amount) || 0;
    if (row.net_amount != null) return Number(row.net_amount) || 0;
    if (row.amount != null) return Number(row.amount) || 0;
    return 0;
  };

  /**
   * Apply a Phase 2 quick-select preset to every aggregated line in the
   * draft. Contractor can adjust individual ticks after this — the buttons
   * only shape the STARTING state.
   *
   * mode:
   *   'allt'     — tick every unbilled row with date <= draft.issue_date.
   *   'timabil'  — tick every unbilled row whose date falls in [from, to].
   *   'upphaed'  — walk unbilled rows ACROSS all aggregated lines sorted
   *                by date ascending; tick oldest-first until the cumulative
   *                net at least reaches the target amount.
   *
   * Any manual-override flags are cleared so the freshly-preset lines
   * pick up their derived quantity/unit_price. All aggregated lines are
   * auto-expanded so the operator can immediately see and adjust what was
   * ticked.
   */
  const applyQuickSelect = (mode, opts = {}) => {
    if (!draft || !sources) return;
    const issueDate = draft.issue_date || new Date().toISOString().slice(0, 10);

    // Phase 1 #1: a row-backed line that ends up with ZERO ticked rows must
    // disappear, exactly like un-ticking its last row by hand does. It used
    // to keep its amount (receipt lines never recompute their price from the
    // ticks), so the invoice charged kr that no row stood behind — nothing
    // got marked billed and the project drifted into a 409.
    const dropEmptyRowLines = (lines) => {
      const removed = lines.filter(
        (l) => isAggregated(l.kind) && (l.source_ids || []).length === 0,
      ).length;
      return {
        kept: lines.filter(
          (l) => !isAggregated(l.kind) || (l.source_ids || []).length > 0,
        ),
        removed,
      };
    };

    const linesForMode = (mode === 'upphaed')
      ? null
      : draft.lines.map((line) => {
          if (!isAggregated(line.kind)) return line;
          const available = sourcesForLine(sources, line, draft.lines);
          let picks;
          if (mode === 'allt') {
            picks = available
              .filter((r) => (r.date || '') <= issueDate)
              .map((r) => r.id);
          } else if (mode === 'timabil') {
            const from = opts.fromDate || '';
            const to = opts.toDate || issueDate;
            picks = available
              .filter((r) => (r.date || '') >= from && (r.date || '') <= to)
              .map((r) => r.id);
          } else {
            picks = line.source_ids || [];
          }
          const derived = deriveLineTotals(line, new Set(picks), sources);
          return { ...line, source_ids: picks, ...derived };
        });

    if (mode === 'upphaed') {
      const target = parseFloat(opts.targetAmount) || 0;
      if (target <= 0) {
        toast.error('Sláðu inn upphæð stærri en 0');
        return;
      }
      // Flatten every aggregated line's rows into one date-sorted list,
      // then tick oldest-first until cumulative net reaches the target.
      const flat = [];
      draft.lines.forEach((line, lineIdx) => {
        if (!isAggregated(line.kind)) return;
        const available = sourcesForLine(sources, line, draft.lines);
        available.forEach((r) => {
          flat.push({ lineIdx, rowId: r.id, date: r.date || '', net: rowNet(r) });
        });
      });
      flat.sort((a, b) => a.date.localeCompare(b.date));

      let cumulative = 0;
      let takenCount = 0;
      const picksByLine = {};
      for (const item of flat) {
        // iter383 #5: never pass the typed amount. 276.000 used to select
        // 288.000 because the last row was taken whenever the running total
        // was still short. Now a row is only taken when it FITS, and the
        // shortfall is reported instead of silently overshooting.
        if (cumulative + item.net > target) continue;
        picksByLine[item.lineIdx] = picksByLine[item.lineIdx] || [];
        picksByLine[item.lineIdx].push(item.rowId);
        cumulative += item.net;
        takenCount += 1;
      }
      if (takenCount === 0) {
        const smallest = flat.length
          ? Math.min(...flat.map((f) => f.net).filter((n) => n > 0))
          : 0;
        toast.error(smallest > 0
          ? `Engin færsla kemst fyrir í ${ISK(target)} kr. Minnsta færslan er ${ISK(smallest)} kr.`
          : 'Engar færslur í boði til að reikningsfæra');
        return;
      }
      const applied = draft.lines.map((line, idx) => {
        if (!isAggregated(line.kind)) return line;
        const picks = picksByLine[idx] || [];
        const derived = deriveLineTotals(line, new Set(picks), sources);
        return { ...line, source_ids: picks, ...derived };
      });
      const { kept, removed } = dropEmptyRowLines(applied);
      setDraft((d) => ({ ...d, lines: kept }));
      const expandAll = {};
      kept.forEach((l) => { if (isAggregated(l.kind)) expandAll[l.id] = true; });
      setExpandedLines((prev) => ({ ...prev, ...expandAll }));
      setManualLines({});
      setQsMode(null);
      // iter385 #2: ONE toast. This used to fire three in a row (selection,
      // shortfall, removed lines) and they stacked on top of each other.
      const parts = [`Valdi ${takenCount} færslur · ${ISK(cumulative)} kr`];
      const shortfall = Math.round(target - cumulative);
      if (shortfall >= 1) parts.push(`Vantar ${ISK(shortfall)} kr upp í upphæðina`);
      if (removed === 1) parts.push('Ein lína fjarlægð, engar færslur valdar á henni');
      else if (removed > 1) parts.push(`${removed} línur fjarlægðar, engar færslur valdar á þeim`);
      toast.success(parts.join(' · '));
      return;
    }

    const { kept, removed } = dropEmptyRowLines(linesForMode);
    setDraft((d) => ({ ...d, lines: kept }));
    const expandAll = {};
    kept.forEach((l) => { if (isAggregated(l.kind)) expandAll[l.id] = true; });
    setExpandedLines((prev) => ({ ...prev, ...expandAll }));
    setManualLines({});
    setQsMode(null);
    // iter385 #2: one toast, never a stack.
    const notes = [];
    if (mode === 'allt') notes.push('Allar færslur t.o.m. útgáfudags valdar');
    else if (mode === 'timabil') notes.push('Færslur á tímabilinu valdar');
    if (removed === 1) notes.push('Ein lína fjarlægð, engar færslur valdar á henni');
    else if (removed > 1) notes.push(`${removed} línur fjarlægðar, engar færslur valdar á þeim`);
    if (notes.length) toast.success(notes.join(' · '));
  };

  /**
   * Server-computed live-preview of the invoice's totals + per-line
   * rounded net/vat/total. The modal used to compute all of this in
   * `computeLineTotals` client-side, but JS `Math.round(.5)` (up)
   * disagrees with Python `round(.5)` (banker's) on halves — the
   * subtotal shown here could differ by 1 kr from the saved invoice
   * on ~2.3% of ordinary inputs. Fix: route through the same
   * `compute_invoice_totals` the write path uses, debounced 250 ms
   * so rapid typing doesn't spam the endpoint.
   *
   * Fallback: while the preview is loading (or on transient network
   * failure) each line displays zeros; the final save recomputes
   * authoritatively so a stale preview never corrupts persisted data.
   */
  const [preview, setPreview] = useState({
    subtotal: 0, vat_total: 0, total: 0, vat_breakdown: {}, lines: [],
  });
  useEffect(() => {
    if (!draft) return undefined;
    const t = setTimeout(async () => {
      try {
        const payload = {
          lines: (draft.lines || []).map((l) => ({
            quantity: Number(l.quantity) || 0,
            unit_price: Number(l.unit_price) || 0,
            vat_rate: Number(l.vat_rate) || 0,
          })),
        };
        const r = await tenantInvoicesAPI.previewTotals(payload);
        setPreview(r.data);
      } catch {
        // Retain last-good — never blank the modal on a network blip.
      }
    }, 250);
    return () => clearTimeout(t);
  }, [draft]);

  /**
   * "Á reikningi" — the invoice's own subtotal (án VSK), straight from the
   * server preview endpoint. Matches the Fjármál "Til reikningsfærslu"
   * convention of quoting net figures.
   *
   * iter358: the old second figure was removed. It was the net value of every
   * unbilled row on the verk that was not ticked here, added up IN THE
   * BROWSER, excluding the fast verð remainder and excluding VSK. Three
   * problems: it is browser money math, it silently left the contract
   * remainder out so it read far too low on a fast verð verk, and "eftir í
   * verkinu" sounds like the contract balance while it actually meant
   * "unticked rows". Fjármál already answers that question properly.
   */
  const runningTotals = useMemo(() => ({
    invoiced_net: preview.subtotal || 0,
  }), [preview.subtotal]);

  // Modal totals — server-authoritative. No JS money math.
  const totals = {
    subtotal: preview.subtotal || 0,
    vat_total: preview.vat_total || 0,
    total: preview.total || 0,
  };

  // Review #1: a line priced at 0 kr is not put on the invoice. The server
  // drops the same lines (`tenant_invoices.drop_zero_lines`), so the fixed
  // price line can arrive at 0 and be left alone without blocking anything.
  const billableLines = (draft?.lines || []).filter(
    (l) => parseFloat(l.unit_price) !== 0,
  );

  const validate = () => {
    if (!draft) return 'Engin gögn';
    if (!draft.customer?.name?.trim()) return 'Vantar nafn viðskiptavinar';
    if (!draft.lines?.length) return 'Bættu við að minnsta kosti einni línu';
    if (!billableLines.length) return 'Sláðu inn upphæð á að minnsta kosti eina línu';
    for (const l of billableLines) {
      if (!(l.description || '').trim()) return 'Vantar lýsingu á línu';
      if (!(parseFloat(l.quantity) > 0)) return 'Magn verður að vera stærra en 0';
    }
    if (!draft.issue_date) return 'Vantar útgáfudag';
    if (!draft.due_date) return 'Vantar eindaga';
    return null;
  };

  const saveDraft = async ({ silent = false } = {}) => {
    const v = validate();
    if (v) { setError(v); return null; }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        project_id: draft.project_id || projectId,
        customer: draft.customer || {},
        lines: billableLines.map(({ id, kind, source, source_id, source_ids, description, quantity, unit, unit_price, vat_rate }) => ({
          id, kind, source, source_id, description, unit,
          quantity: parseFloat(quantity) || 0,
          unit_price: parseFloat(unit_price) || 0,
          vat_rate: parseInt(vat_rate, 10) || 0,
          // Preserve source_ids so recompute_billed_state on the backend can
          // correctly derive which time_entries/receipts/materials are billed.
          // Prior to this fix source_ids were silently dropped by destructuring,
          // which meant saved drafts had empty source_ids → no rows marked billed.
          source_ids: Array.isArray(source_ids) ? source_ids : [],
        })),
        issue_date: draft.issue_date,
        due_date: draft.due_date,
        final_due_date: draft.final_due_date || null,
        notes_public: draft.notes_public || '',
        notes_internal: draft.notes_internal || '',
        // iter353: stored on the invoice. "Merkja sem reikningsfært" is
        // refused server-side if the lines drift away from this list.
        selection,
        idempotency_key: idempotencyKeyRef.current,
      };
      let saved;
      if (persistedId) {
        const r = await tenantInvoicesAPI.update(persistedId, payload);
        saved = r.data;
      } else {
        const r = await tenantInvoicesAPI.create(payload);
        saved = r.data;
        setPersistedId(saved.invoice_id);
      }
      if (!silent) toast.success('Drög vistuð');
      return saved;
    } catch (e) {
      const detail = e.response?.data?.detail || 'Vistun mistókst';
      if (e.response?.status === 409) setInvariantBlocked(true);
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async ({ forceManual = false } = {}) => {
    setSending(true);
    try {
      // Persist any pending edits first
      const saved = await saveDraft({ silent: true });
      if (!saved) { setSending(false); return; }
      const r = await tenantInvoicesAPI.send(saved.invoice_id, { force_manual: forceManual });
      const status = r.data?.status;
      if (status === 'sent') {
        toast.success('Reikningur sendur í Konto');
      } else if (status === 'sent_manual') {
        toast.success(T.toastManual);
      }
      onSent?.(r.data);
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Sending mistókst';
      if (e.response?.status === 409) setInvariantBlocked(true);
      const code = detail?.code || (typeof detail === 'string' ? detail : 'send_failed');
      if (code === 'konto_send_failed') {
        setError(detail.message || 'Konto sending mistókst, þú getur smellt á "Merkja sem reikningsfært" til að halda áfram utan kerfis.');
      } else {
        setError(typeof detail === 'string' ? detail : (detail.message || JSON.stringify(detail)));
      }
    } finally {
      setSending(false);
      setConfirmSend(false);
    }
  };

  // ── Export helpers (PDF / CSV / clipboard) ──────────────────────────────
  // These persist any pending edits first, then hit the corresponding backend
  // export endpoint. All three are safe to call repeatedly; nothing is
  // permanently altered. Copy-to-clipboard uses a local text formatter so it
  // works even when the user is offline.
  const [exporting, setExporting] = useState(null); // 'pdf' | 'csv' | null

  const _persistBeforeExport = async () => {
    if (!draft) return null;
    const saved = await saveDraft({ silent: true });
    return saved || null;
  };

  const _downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const downloadSummaryPdf = async () => {
    setExporting('pdf');
    try {
      const saved = await _persistBeforeExport();
      const id = saved?.invoice_id || persistedId;
      if (!id) { toast.error('Ekkert til að flytja út enn.'); return; }
      const r = await tenantInvoicesAPI.verkyfirlitPdf(id);
      const projName = draft?.project?.name || 'reikningsfaersla';
      const safe = String(projName).replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 60);
      _downloadBlob(r.data, `Rektra-samantekt-${safe}.pdf`);
      toast.success('PDF sótt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF útflutningur mistókst');
    } finally {
      setExporting(null);
    }
  };

  const downloadSummaryCsv = async () => {
    setExporting('csv');
    try {
      const saved = await _persistBeforeExport();
      const id = saved?.invoice_id || persistedId;
      if (!id) { toast.error('Ekkert til að flytja út enn.'); return; }
      const r = await tenantInvoicesAPI.summaryCsv(id);
      const projName = draft?.project?.name || 'reikningsfaersla';
      const safe = String(projName).replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 60);
      _downloadBlob(new Blob([r.data], { type: 'text/csv;charset=utf-8' }), `Rektra-samantekt-${safe}.csv`);
      toast.success('CSV sótt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CSV útflutningur mistókst');
    }
    setExporting(null);
  };

  const copySummaryToClipboard = async () => {
    if (!draft) return;
    // Per-line + grand total come from the server preview — same
    // rule as elsewhere in this modal: zero client-side money math.
    // Using `preview.lines[idx].net` (already rounded per line) and
    // `preview.subtotal` / `preview.vat_total` / `preview.total` (all
    // server-rounded sums) means the pasted string is byte-identical
    // to what the saved invoice will store, even for tenants (or
    // values) hitting the JS-vs-Python .5-rounding divergence.
    //
    // Iter309: reads live edited `draft.lines[]` — any inline edit
    // made in the modal (added/removed lines, adjusted quantities,
    // renamed descriptions) is already reflected here.
    const fmt = (n) => formatISK(Math.round(Number(n) || 0));
    const lines = (draft.lines || []).map((l, idx) => {
      const total = preview.lines?.[idx]?.net ?? 0;
      return `- ${l.description || '(engin lýsing)'}: ${fmt(total)} kr`;
    });
    const subtotal = preview.subtotal || 0;
    const vatTotal = preview.vat_total || 0;
    const total = preview.total || (subtotal + vatTotal);
    const proj = draft.project?.name || '';
    const customer = draft.customer?.name || '';
    const parts = [
      proj ? `Verkefni: ${proj}` : null,
      customer ? `Viðskiptavinur: ${customer}` : null,
      '',
      ...lines,
      '',
      `Samtals án VSK: ${fmt(subtotal)} kr`,
      `VSK: ${fmt(vatTotal)} kr`,
      `Samtals með VSK: ${fmt(total)} kr`,
    ].filter((x) => x !== null);
    const text = parts.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Afritað, límdu í Konto / Payday / DK');
    } catch {
      toast.error('Afritun mistókst');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-foreground-muted" />
        </div>
      );
    }
    if (selectionError) {
      return (
        <div className="p-5">
          <div
            className="p-4 bg-error/10 border border-error/30 rounded-lg text-sm space-y-3"
            data-testid="invoice-selection-error"
          >
            <div className="flex items-start gap-2 text-error">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">{selectionError}</div>
            </div>
            <p className="text-xs text-foreground-muted">
              Ekkert hefur verið reikningsfært. Engin lína er sýnd hér, því
              annars væri hætta á að reikningsfæra annað en þú valdir.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => { setSelectionError(null); loadDraft({ scoped: false }); }}
                className="btn btn-secondary"
                data-testid="invoice-selection-retry"
              >
                Reyna aftur
              </button>
              <button onClick={onClose} className="btn btn-tertiary" data-testid="invoice-selection-close">
                Loka
              </button>
            </div>
          </div>
        </div>
      );
    }
    if (!draft) return null;
    return (
      <div className="space-y-5 p-5">
        {error && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm flex items-start gap-2" data-testid="invoice-editor-error">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-error/70 hover:text-error"><X size={14} /></button>
          </div>
        )}

        {/* Unbilled summary + date-range scope (only when prefilling from a project) */}
        {projectId && !invoiceId && summary && (
          <section className="card p-4 space-y-3 border border-accent/20 bg-accent/5" data-testid="invoice-editor-summary">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FileText size={14} className="text-accent" /> Óreikningsfært á verkinu
              </h3>
              {summary.billing_type === 'fixed' && summary.fixed_price > 0 && (
                <span className="text-[11px] text-foreground-muted">
                  Heildarverk: <span className="font-mono">{ISK(summary.fixed_price)} kr</span>
                  {summary.fixed_invoiced > 0 && (
                    <> · Reikningsfært: <span className="font-mono text-emerald-400">{ISK(summary.fixed_invoiced)} kr</span></>
                  )}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {summary.billing_type === 'fixed' ? (
                <div className="p-2 bg-background rounded-md">
                  <p className="text-foreground-muted text-[10px] uppercase">Eftirstöðvar</p>
                  <p className="font-mono font-semibold" data-testid="summary-fixed-remaining">{ISK(summary.fixed_remaining)} kr</p>
                </div>
              ) : (
                <div className="p-2 bg-background rounded-md">
                  <p className="text-foreground-muted text-[10px] uppercase">Óreikningsfærðir tímar</p>
                  <p className="font-mono font-semibold" data-testid="summary-unbilled-hours">{formatKlst(summary.unbilled_hours)}</p>
                </div>
              )}
              {summary.unbilled_extra_hours > 0 && (
                <div className="p-2 bg-background rounded-md">
                  <p className="text-foreground-muted text-[10px] uppercase">Aukavinna</p>
                  <p className="font-mono font-semibold text-amber-400" data-testid="summary-extra-hours">{formatKlst(summary.unbilled_extra_hours)}</p>
                </div>
              )}
              {summary.unbilled_materials_amount > 0 && (
                <div className="p-2 bg-background rounded-md">
                  <p className="text-foreground-muted text-[10px] uppercase">Efni</p>
                  <p className="font-mono font-semibold" data-testid="summary-materials">{ISK(summary.unbilled_materials_amount)} kr</p>
                </div>
              )}
              {summary.unbilled_receipts_count > 0 && (
                <div className="p-2 bg-background rounded-md">
                  <p className="text-foreground-muted text-[10px] uppercase">Kvittanir</p>
                  <p className="font-mono font-semibold" data-testid="summary-receipts">{summary.unbilled_receipts_count} stk</p>
                </div>
              )}
            </div>

            {/* Review #6: the old Frá/Til + "Sækja gögn" box is gone. The
                Allt / Tímabil / Upphæð picker below does the same job on the
                rows that are already in the draft, without a server round
                trip that silently rebuilt the whole thing. */}
          </section>
        )}

        {/* Customer block — essentials always visible; address/zip/city are
            collapsed behind an "Ítarleg upplýsingar" toggle since the tenant
            doesn't need them to mark a reikningsfærsla (they only matter
            if the invoice ends up as a mailed / kröfu-linked Konto doc).
            Auto-expands whenever any of those fields already has a value
            so users editing an existing draft never see fields disappear. */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide">Viðskiptavinur</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              className="input"
              placeholder="Nafn viðskiptavinar"
              value={draft.customer.name || ''}
              onChange={(e) => setCustomer('name', e.target.value)}
              data-testid="invoice-customer-name"
            />
            <input
              className="input"
              placeholder="Kennitala"
              value={draft.customer.kennitala || ''}
              onChange={(e) => setCustomer('kennitala', e.target.value)}
              data-testid="invoice-customer-kennitala"
            />
            <input
              className="input"
              placeholder="Netfang"
              value={draft.customer.email || ''}
              onChange={(e) => setCustomer('email', e.target.value)}
              data-testid="invoice-customer-email"
            />
            <input
              className="input"
              placeholder="Sími"
              value={draft.customer.phone || ''}
              onChange={(e) => setCustomer('phone', e.target.value)}
              data-testid="invoice-customer-phone"
            />
          </div>
          {(() => {
            const hasAdvanced = !!(draft.customer.address || draft.customer.postal_code || draft.customer.city);
            const expanded = showAdvancedCustomer || hasAdvanced;
            return (
              <>
                {!expanded && (
                  <button
                    type="button"
                    onClick={() => setShowAdvancedCustomer(true)}
                    className="text-xs text-foreground-muted hover:text-foreground inline-flex items-center gap-1"
                    data-testid="invoice-toggle-advanced-customer"
                  >
                    <ChevronDown size={12} /> Ítarleg upplýsingar (heimilisfang, póstnúmer, sveitarfélag)
                  </button>
                )}
                {expanded && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <input
                      className="input sm:col-span-2"
                      placeholder="Heimilisfang"
                      value={draft.customer.address || ''}
                      onChange={(e) => setCustomer('address', e.target.value)}
                      data-testid="invoice-customer-address"
                    />
                    <input
                      className="input"
                      placeholder="Póstnúmer"
                      value={draft.customer.postal_code || ''}
                      onChange={(e) => setCustomer('postal_code', e.target.value)}
                      data-testid="invoice-customer-postnr"
                    />
                    <input
                      className="input"
                      placeholder="Sveitarfélag"
                      value={draft.customer.city || ''}
                      onChange={(e) => setCustomer('city', e.target.value)}
                      data-testid="invoice-customer-city"
                    />
                  </div>
                )}
              </>
            );
          })()}
        </section>

        {/* Lines */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide">Línur</h3>
            <button
              onClick={addLine}
              className="text-xs font-medium px-2 py-1 rounded-md bg-accent/10 text-accent hover:bg-accent/20 flex items-center gap-1"
              data-testid="invoice-add-line"
            >
              <Plus size={14} /> Bæta við línu
            </button>
          </div>

          {/* Phase 2 — quick-select bar. Contractor still adjusts individual
              ticks after; these only shape the STARTING state. Hidden for a
              selection-scoped draft: "Allt" there would tick rows the operator
              never selected, and the server would then refuse to mark it. */}
          {!selection && sources && draft.lines.some((l) => isAggregated(l.kind)) && (
            <div className="rounded-lg border border-border bg-background/30 p-2 space-y-2" data-testid="invoice-quickselect-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase text-foreground-muted mr-1">Velja færslur:</span>
                <button
                  onClick={() => applyQuickSelect('allt')}
                  className="text-xs font-medium px-2.5 py-1.5 rounded-md bg-accent/10 text-accent hover:bg-accent/20"
                  data-testid="invoice-quickselect-allt"
                  title={`Velja allt til og með ${draft.issue_date || 'útgáfudags'}`}
                >
                  Allt
                </button>
                <button
                  onClick={() => setQsMode((m) => (m === 'timabil' ? null : 'timabil'))}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-md ${qsMode === 'timabil' ? 'bg-accent/25 text-accent' : 'bg-accent/10 text-accent hover:bg-accent/20'}`}
                  data-testid="invoice-quickselect-timabil-toggle"
                >
                  Tímabil
                </button>
                <button
                  onClick={() => setQsMode((m) => (m === 'upphaed' ? null : 'upphaed'))}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-md ${qsMode === 'upphaed' ? 'bg-accent/25 text-accent' : 'bg-accent/10 text-accent hover:bg-accent/20'}`}
                  data-testid="invoice-quickselect-upphaed-toggle"
                >
                  Upphæð
                </button>
              </div>

              {qsMode === 'timabil' && (
                <div className="flex flex-wrap items-end gap-2 pt-1" data-testid="invoice-quickselect-timabil-panel">
                    <div className="flex-1 min-w-[120px]">
                      <DateFieldIS
                        label="Frá"
                        value={qsFromDate}
                        onChange={setQsFromDate}
                        testId="invoice-quickselect-from"
                      />
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <DateFieldIS
                        label="Til"
                        value={qsToDate}
                        onChange={setQsToDate}
                        testId="invoice-quickselect-to"
                      />
                    </div>
                  <button
                    onClick={() => {
                      if (!qsFromDate || !qsToDate) {
                        toast.error('Veldu bæði upphaf og lok tímabils');
                        return;
                      }
                      if (qsFromDate > qsToDate) {
                        toast.error('„Frá“ dagsetning verður að vera á undan „Til“');
                        return;
                      }
                      applyQuickSelect('timabil', { fromDate: qsFromDate, toDate: qsToDate });
                    }}
                    className="text-xs font-medium px-3 py-2 rounded-md bg-accent text-background hover:bg-accent-hover"
                    data-testid="invoice-quickselect-timabil-apply"
                  >
                    Velja
                  </button>
                </div>
              )}

              {qsMode === 'upphaed' && (
                <div className="flex flex-wrap items-end gap-2 pt-1" data-testid="invoice-quickselect-upphaed-panel">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-[10px] uppercase text-foreground-muted">
                      Upphæð (án VSK)
                    </label>
                    <MoneyInput
                      className="input"
                      value={qsAmount}
                      placeholder="t.d. 300.000"
                      onChange={setQsAmount}
                      testId="invoice-quickselect-amount"
                    />
                  </div>
                  <button
                    onClick={() => {
                      const n = parseFloat(qsAmount);
                      if (!n || n <= 0) {
                        toast.error('Sláðu inn upphæð stærri en 0');
                        return;
                      }
                      applyQuickSelect('upphaed', { targetAmount: n });
                    }}
                    className="text-xs font-medium px-3 py-2 rounded-md bg-accent text-background hover:bg-accent-hover"
                    data-testid="invoice-quickselect-upphaed-apply"
                  >
                    Velja
                  </button>
                  <p className="w-full text-[11px] text-foreground-muted mt-1">
                    Velur elstu færslur þar til samtalan nær upphæðinni. Þú getur aftikkað einstakar færslur á eftir.
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="space-y-2">
            {draft.lines.length === 0 && (
              <div className="text-center py-8 text-foreground-muted text-sm border border-dashed border-border rounded-lg">
                Engar línur, smelltu á <span className="text-accent">+ Bæta við línu</span> til að byrja.
              </div>
            )}
            {draft.lines.map((line, idx) => {
              // iter377: a company that is not on the VAT register may not put
              // VAT on any line, so the dropdown is locked at 0 %.
              // Per-line rounded net/vat/total come from the server
              // preview — the frontend must NOT recompute money math.
              // Fallback zeros while the debounced preview is in-flight
              // or on transient network failure; the final save
              // recomputes authoritatively either way.
              const t = preview.lines?.[idx] || { net: 0, vat: 0, total: 0 };
              const aggregated = isAggregated(line.kind);
              const availableRows = aggregated ? sourcesForLine(sources, line, draft.lines) : [];
              const tickedSet = new Set(line.source_ids || []);
              // Union of ticked ids + available rows — so a ticked row that
              // is no longer in the sources feed (e.g. was deleted) still
              // renders with a "vantar" flag so the user can un-tick it.
              const rowsById = new Map();
              for (const r of availableRows) rowsById.set(r.id, r);
              for (const sid of tickedSet) {
                if (!rowsById.has(sid)) rowsById.set(sid, { id: sid, missing: true });
              }
              const orderedRows = Array.from(rowsById.values()).sort((a, b) => {
                if (a.missing && !b.missing) return 1;
                if (!a.missing && b.missing) return -1;
                return String(a.date || '').localeCompare(String(b.date || ''));
              // iter353: a selection-scoped draft lists the selected rows and
              // nothing else. Rows the operator did not pick have no business
              // on this invoice, not even greyed out.
              }).filter((row) => !selection || tickedSet.has(row.id));
              const isExpanded = !!expandedLines[line.id || idx];
              const isManualOverride = !!manualLines[line.id];
              const tickedCount = tickedSet.size;
              const rowKind = (line.kind || '').toLowerCase();
              // Aggregated hours/aukavinna lines: only `quantity` is
              // checkbox-derived (unit_price is the fixed hourly rate).
              // Material aggregate: both quantity (=1) and unit_price are
              // derived from ticked rows. Receipt lines: both are fixed
              // per source, so they behave the same as material for the
              // purpose of the lock UI. We gate qty for all aggregated
              // kinds and unit_price only for material.
              const qtyLocked = aggregated && !isManualOverride;
              const priceLocked = aggregated && rowKind === 'material' && !isManualOverride;
              return (
                <div key={line.id || idx} className={`card p-3 space-y-2 ${parseFloat(line.unit_price) === 0 ? 'opacity-70 border-dashed' : ''}`} data-testid={`invoice-line-${idx}`}>
                  {/* Review #1: the fixed-price line arrives at 0 kr so nobody
                      can bill a whole tilboð with one tap. The remaining
                      contract is a hint, and the line is left off the invoice
                      until an amount is typed. */}
                  {line.source === 'project_fixed_price' && (
                    <div className="p-2 bg-background rounded-md space-y-1.5" data-testid={`invoice-line-${idx}-fixed-hint`}>
                      <p className="text-xs text-foreground-muted">
                        Eftirstöðvar samnings: <span className="font-mono text-foreground">{ISK(line.suggested_unit_price || 0)} kr</span>
                      </p>
                      <p className="text-[11px] text-amber-400">
                        {parseFloat(line.unit_price) === 0
                          ? 'Sláðu inn upphæðina sem á að reikningsfæra núna. Línan fer ekki á reikninginn á meðan hún stendur í 0 kr.'
                          : 'Þessi upphæð fer á reikninginn.'}
                      </p>
                      {parseFloat(line.unit_price) === 0 && (line.suggested_unit_price || 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => setLine(idx, { unit_price: line.suggested_unit_price })}
                          className="text-[11px] font-medium text-accent hover:underline"
                          data-testid={`invoice-line-${idx}-fixed-use-remaining`}
                        >
                          Reikningsfæra allar eftirstöðvar
                        </button>
                      )}
                    </div>
                  )}
                  {/* iter326: nudge when aukavinna line lacks a rate — prompts
                      the operator to type the hourly rate before sending. */}
                  {line.needs_rate && (Number(line.unit_price) || 0) === 0 && (
                    <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-md space-y-2" data-testid={`invoice-line-${idx}-needs-rate`}>
                      <p className="text-xs text-amber-400">
                        Verkefnið er ekki með aukavinnutaxta. Sláðu hann inn hér,
                        hann vistast á verkefnið og gildir líka næst.
                      </p>
                      {canSetProjectRate ? (
                      <div className="flex items-end gap-2">
                        <div className="flex-1 min-w-[120px]">
                          <label className="text-[10px] uppercase text-foreground-muted">Aukavinna, kr/klst</label>
                          <MoneyInput
                            className="input"
                            value={extraRateInput}
                            onChange={setExtraRateInput}
                            placeholder="t.d. 12.500"
                            testId={`invoice-line-${idx}-extra-rate-input`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => saveExtraWorkRate(idx)}
                          disabled={savingExtraRate}
                          className="text-xs font-medium px-3 py-2 rounded-md bg-accent text-background hover:bg-accent-hover disabled:opacity-50"
                          data-testid={`invoice-line-${idx}-extra-rate-save`}
                        >
                          {savingExtraRate ? 'Vista…' : 'Vista taxta'}
                        </button>
                      </div>
                      ) : (
                        <p className="text-[11px] text-foreground-muted" data-testid={`invoice-line-${idx}-extra-rate-readonly`}>
                          Aðeins eigandi eða stjórnandi getur vistað taxtann á verkefnið.
                          Sláðu upphæðina inn í „Verð / einingu" hér fyrir ofan.
                        </p>
                      )}
                    </div>
                  )}
                  <input
                    className="input"
                    placeholder="Lýsing (t.d. Vinna 5.–9. júní)"
                    value={line.description}
                    onChange={(e) => setLine(idx, { description: e.target.value })}
                    data-testid={`invoice-line-${idx}-desc`}
                  />
                  {/* iter353: 2 columns on a phone so the "Verð / einingu"
                      label cannot wrap into the field below it. */}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase text-foreground-muted">Magn</label>
                      <input
                        type={qtyLocked ? 'text' : 'number'}
                        step="0.01" min="0"
                        readOnly={qtyLocked}
                        tabIndex={qtyLocked ? -1 : 0}
                        className={qtyLocked ? 'input font-mono opacity-70 cursor-not-allowed' : 'input'}
                        value={qtyLocked && (line.unit || '') === 'klst'
                          ? formatHoursNum(line.quantity)
                          : line.quantity}
                        onChange={(e) => setLine(idx, { quantity: e.target.value })}
                        data-testid={`invoice-line-${idx}-qty`}
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase text-foreground-muted">Eining</label>
                      <select
                        className="input"
                        value={line.unit || 'stk'}
                        onChange={(e) => setLine(idx, { unit: e.target.value })}
                        data-testid={`invoice-line-${idx}-unit`}
                      >
                        <option value="klst">klst</option>
                        <option value="stk">stk</option>
                        <option value="verk">verk</option>
                        <option value="kg">kg</option>
                        <option value="m">m</option>
                        <option value="m2">m²</option>
                        <option value="m3">m³</option>
                      </select>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase text-foreground-muted">Verð / einingu</label>
                      {priceLocked ? (
                        <input
                          type="text"
                          readOnly
                          tabIndex={-1}
                          className="input font-mono opacity-70 cursor-not-allowed"
                          value={ISK(line.unit_price)}
                          data-testid={`invoice-line-${idx}-price`}
                        />
                      ) : (
                        <MoneyInput
                          className="input"
                          value={line.unit_price}
                          onChange={(v) => setLine(idx, { unit_price: v })}
                          testId={`invoice-line-${idx}-price`}
                        />
                      )}
                    </div>
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase text-foreground-muted">VSK</label>
                      <select
                        className="input"
                        value={vatLocked ? 0 : (line.vat_rate ?? 24)}
                        disabled={vatLocked}
                        onChange={(e) => setLine(idx, { vat_rate: parseInt(e.target.value, 10) })}
                        data-testid={`invoice-line-${idx}-vat`}
                      >
                        {(vatLocked ? VAT_OPTIONS.filter((o) => o.value === 0) : VAT_OPTIONS).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {vatLocked && (
                        <p
                          className="text-[10px] text-foreground-muted mt-1"
                          data-testid={`invoice-line-${idx}-vat-locked`}
                        >
                          Fyrirtækið er ekki á VSK-skrá og má ekki setja VSK á reikning.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1 text-xs sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-foreground-muted">
                      Án VSK: <span className="font-mono text-foreground">{ISK(t.net)} kr</span>
                      <span className="mx-1">·</span>
                      VSK: <span className="font-mono text-foreground">{ISK(t.vat)} kr</span>
                    </span>
                    <div className="flex items-center gap-3 self-end sm:self-auto">
                      <span className="font-mono font-semibold text-foreground" data-testid={`invoice-line-${idx}-total`}>
                        {ISK(t.total)} kr
                      </span>
                      {aggregated && !isManualOverride && (
                        <button
                          onClick={() => unlockLineManualEntry(idx)}
                          className="flex items-center gap-1 text-[11px] text-accent hover:text-accent-hover"
                          title="Slá inn eigin upphæð (t.d. rúna, veita afslátt eða reikningsfæra fast verð)"
                          data-testid={`invoice-line-${idx}-unlock`}
                        >
                          <Pencil size={12} /> Breyta upphæð
                        </button>
                      )}
                      {aggregated && isManualOverride && (
                        <button
                          onClick={() => relockLineManualEntry(idx)}
                          className="flex items-center gap-1 text-[11px] text-foreground-muted hover:text-foreground"
                          title="Endurreikna magn og verð úr völdum færslum"
                          data-testid={`invoice-line-${idx}-relock`}
                        >
                          <RotateCcw size={12} /> Endurstilla
                        </button>
                      )}
                      <button
                        onClick={() => removeLine(idx)}
                        className="text-foreground-muted hover:text-error"
                        title="Fjarlægja línu"
                        data-testid={`invoice-line-${idx}-remove`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {aggregated && isManualOverride && (
                    <div
                      className="text-[11px] rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-100 px-2 py-1.5 flex gap-2"
                      data-testid={`invoice-line-${idx}-manual-note`}
                    >
                      <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                      <span>
                        Þú ert að rukka fyrir handvirka upphæð. Tikkin niðri
                        ráða áfram hvaða færslur teljast uppgjörsfærðar —
                        rukkuð upphæð og uppgjörsfærðar færslur eru aðskildar.
                      </span>
                    </div>
                  )}

                  {aggregated && (
                    <div className="-mx-3 border-t border-border">
                      <button
                        type="button"
                        onClick={() => toggleLineExpansion(line.id || idx)}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs text-foreground-muted hover:text-foreground hover:bg-card-hover"
                        data-testid={`invoice-line-${idx}-expand`}
                        aria-expanded={isExpanded}
                      >
                        <span className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span>
                            {tickedCount} {tickedCount === 1 ? 'færsla valin' : 'færslur valdar'}
                            {orderedRows.length > tickedCount && (
                              <span className="text-foreground-muted"> · {orderedRows.length - tickedCount} í boði</span>
                            )}
                          </span>
                        </span>
                        <span className="text-[10px]">
                          {isExpanded ? 'Fela færslur' : 'Sjá færslur'}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="max-h-[50dvh] overflow-y-auto divide-y divide-border" data-testid={`invoice-line-${idx}-sources`}>
                          <div className="px-3 py-2 text-[11px] text-foreground-muted bg-background/40 border-b border-border">
                            {selection ? (
                              'Þetta eru nákvæmlega færslurnar sem þú valdir í skýrslunni. '
                              + 'Til að breyta valinu skaltu loka glugganum og velja aftur í skýrslunni.'
                            ) : (
                              <>
                            Að merkja / afmerkja færslu ræður hvað telst uppgjörsfært. Magn og verð uppfærast sjálfkrafa út frá tikkinu, smelltu á „Breyta upphæð“ ef þú vilt rukka aðra fjárhæð (rúnun, afsláttur, fast verð).
                              </>
                            )}
                          </div>
                          {orderedRows.length === 0 && (
                            <div className="px-3 py-3 text-xs text-foreground-muted text-center">
                              Engar færslur eftir að reikningsfæra á þessu verkefni.
                            </div>
                          )}
                          {orderedRows.map((row) => {
                            const checked = tickedSet.has(row.id);
                            return (
                              <label
                                key={row.id}
                                className={`flex items-start gap-3 px-3 py-3 min-h-[44px] ${selection ? '' : 'cursor-pointer hover:bg-card-hover'} ${row.missing ? 'opacity-60' : ''}`}
                                data-testid={`invoice-line-${idx}-source-${row.id}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={!!selection}
                                  onCheckedChange={() => { if (!selection) toggleSource(idx, row.id); }}
                                  className="mt-0.5 h-5 w-5"
                                  data-testid={`invoice-line-${idx}-source-${row.id}-toggle`}
                                />
                                <div className="flex-1 min-w-0">
                                  {row.missing ? (
                                    <div className="text-xs text-amber-400">
                                      {row.id} <span className="text-foreground-muted">(færsla ekki lengur til, aftikkaðu til að fjarlægja)</span>
                                    </div>
                                  ) : rowKind === 'hours' || rowKind === 'aukavinna' ? (
                                    <>
                                      <div className="text-sm truncate">
                                        {formatShortDate(row.date)}{' '}
                                        {row.employee_name && <span className="text-foreground-muted">{row.employee_name}, </span>}
                                        <span className="font-mono">
                                          {rowKind === 'aukavinna' && !row.is_extra_work && row.extra_work_hours > 0
                                            ? `${formatKlst(row.extra_work_hours)} (af ${formatKlst(row.hours)})`
                                            : formatKlst(row.hours)}
                                        </span>
                                      </div>
                                      {row.description && (
                                        <div className="text-[11px] text-foreground-muted truncate">{row.description}</div>
                                      )}
                                    </>
                                  ) : rowKind === 'receipt' ? (
                                    <div className="text-sm truncate">
                                      {formatShortDate(row.date)} <span className="text-foreground-muted">{row.store_name}</span>
                                      {row.is_refund && <span className="text-[10px] ml-1 px-1 rounded bg-amber-400/10 text-amber-400">endurgreiðsla</span>}
                                    </div>
                                  ) : rowKind === 'material' ? (
                                    <>
                                      <div className="text-sm truncate">
                                        {formatShortDate(row.date)} <span className="text-foreground-muted">{row.description || 'Efni'}</span>
                                      </div>
                                      <div className="text-[11px] text-foreground-muted">
                                        {row.quantity} × {ISK(row.unit_price)} kr
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                {!row.missing && (
                                  <div className="text-xs font-mono text-right shrink-0">
                                    {ISK(rowKind === 'material' ? row.net_amount : (rowKind === 'receipt' ? row.net_amount : row.amount))} kr
                                  </div>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Phase 2 — live running total. án VSK on both sides matches
              Fjármál "Til reikningsfærslu" convention. */}
          {sources && draft.lines.some((l) => isAggregated(l.kind)) && (
            <div
              className="text-[11px] rounded-md border border-border bg-background/30 px-3 py-2 flex flex-wrap items-center justify-between gap-2"
              data-testid="invoice-running-total"
            >
              <span className="text-foreground-muted">
                Á reikningi: <span className="font-mono text-foreground" data-testid="invoice-running-invoiced">{ISK(runningTotals.invoiced_net)} kr</span>
                <span className="text-foreground-muted"> (án VSK)</span>
              </span>
            </div>
          )}
        </section>

        {/* iter385 #2: one banner at a time. The error banner above takes
            precedence, so the VSK question never stacks under it. */}
        {askVatRegistered && !error && (
          <div
            className="p-3 rounded-md border border-amber-500/40 bg-amber-500/10 space-y-2"
            data-testid="invoice-ask-vat-registered"
          >
            <p className="text-sm font-medium text-foreground">Er fyrirtækið á VSK-skrá?</p>
            <p className="text-xs text-foreground-muted">
              Svarið ræður því hvort VSK fer á reikningslínur. Þú getur breytt því
              síðar í Stillingum.
            </p>
            <div className="flex gap-2">
              <button
                className="btn btn-primary text-xs"
                disabled={vatAnswerSaving}
                onClick={() => answerVatRegistered(true)}
                data-testid="invoice-vat-registered-yes"
              >
                Já
              </button>
              <button
                className="btn btn-secondary text-xs"
                disabled={vatAnswerSaving}
                onClick={() => answerVatRegistered(false)}
                data-testid="invoice-vat-registered-no"
              >
                Nei
              </button>
            </div>
          </div>
        )}

        {/* Dates */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide">Dagsetningar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DateFieldIS
              label="Útgáfudagur"
              value={draft.issue_date}
              onChange={(iso) => setDraft({ ...draft, issue_date: iso })}
              testId="invoice-issue-date"
            />
            <DateFieldIS
              label="Eindagi"
              value={draft.due_date}
              onChange={(iso) => setDraft({ ...draft, due_date: iso })}
              testId="invoice-due-date"
            />
            <DateFieldIS
              label="Lokaeindagi (val)"
              value={draft.final_due_date}
              onChange={(iso) => setDraft({ ...draft, final_due_date: iso })}
              testId="invoice-final-due-date"
            />
          </div>
        </section>

        {/* Notes — for non-allowlisted tenants we drop all "sést hjá viðskiptavini"
            language because there IS no send-to-customer path. Notes end up
            purely on the PDF/CSV samantekt the user prints or hands to the
            bookkeeper. Reynir dogfood keeps the invoice-oriented wording. */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide">
            {integrationsAllowed ? 'Skilaboð á reikning' : 'Athugasemd'}
          </h3>
          <textarea
            className="input min-h-[72px]"
            placeholder={integrationsAllowed
              ? 'T.d. takk fyrir viðskiptin, ekki birtast hjá viðskiptavini ef autt'
              : 'T.d. sundurliðun eða þakkir, sést á samantektinni'}
            value={draft.notes_public || ''}
            onChange={(e) => setDraft({ ...draft, notes_public: e.target.value })}
            data-testid="invoice-notes-public"
          />
          <textarea
            className="input min-h-[48px] text-xs"
            placeholder={integrationsAllowed
              ? 'Innri athugasemd (sést EKKI hjá viðskiptavini)'
              : 'Innri athugasemd (aðeins fyrir þig)'}
            value={draft.notes_internal || ''}
            onChange={(e) => setDraft({ ...draft, notes_internal: e.target.value })}
            data-testid="invoice-notes-internal"
          />
        </section>

        {/* Totals */}
        <section className="card p-4 bg-background border border-border space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-foreground-muted">Án VSK</span>
            <span className="font-mono" data-testid="invoice-totals-subtotal">{ISK(totals.subtotal)} kr</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-foreground-muted">VSK</span>
            <span className="font-mono" data-testid="invoice-totals-vat">{ISK(totals.vat_total)} kr</span>
          </div>
          <div className="border-t border-border my-2" />
          <div className="flex justify-between text-lg font-semibold">
            <span>Heildarverð</span>
            <span className="font-mono text-emerald-400" data-testid="invoice-totals-total">{ISK(totals.total)} kr</span>
          </div>
        </section>
      </div>
    );
  };

  return (
    <PortalOverlay open={open} onClose={onClose} placement="center" testId="invoice-editor-modal">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-screen h-[100dvh] sm:w-[min(720px,95vw)] sm:h-auto sm:max-h-[90dvh] flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 pt-[max(1rem,env(safe-area-inset-top))] border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-accent" />
            <div>
              <h2 className="text-base font-semibold" data-testid="invoice-editor-title">
                {persistedId ? T.titleEdit : T.titleNew}
              </h2>
              <p className="text-xs text-foreground-muted">{T.subtitle}</p>
              {/* iter320: aukaverk-only mode banner — makes it obvious the
                  invoice is scoped to the picked aukaverk rows only. */}
              {!persistedId && Array.isArray(preSelectedEntryIds) && preSelectedEntryIds.length > 0 && (
                <div
                  className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 text-[11px] font-medium"
                  data-testid="invoice-editor-aukaverk-only-badge"
                >
                  <Zap size={11} />
                  Aukaverk-reikningur · {preSelectedEntryIds.length} færsl{preSelectedEntryIds.length === 1 ? 'a' : 'ur'}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-card-hover rounded-lg" data-testid="invoice-editor-close">
            <X size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>

        {/* Footer */}
        {!loading && draft && (
          <footer className="px-5 py-4 border-t border-border bg-card sticky bottom-0 z-10 space-y-2">
            {confirmSend ? (
              <div className="p-3 bg-amber-400/10 border border-amber-400/30 rounded-lg space-y-2" data-testid="invoice-send-confirm">
                <p className="text-sm">
                  <strong>Ertu viss?</strong> Reikningurinn verður sendur með Konto til <strong>{draft.customer?.name || 'viðskiptavinar'}</strong>.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => sendNow({ forceManual: false })}
                    disabled={sending}
                    className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="invoice-send-confirm-yes"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Já, senda núna
                  </button>
                  <button
                    onClick={() => setConfirmSend(false)}
                    disabled={sending}
                    className="btn btn-secondary sm:w-32"
                    data-testid="invoice-send-confirm-no"
                  >
                    Hætta við
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Iter309: three equal-weight primary actions on the SAME
                    edited data. Copy summary is no longer gated on persist —
                    the button reads from `draft.lines` (live edited state)
                    + server-computed preview totals. `Senda með Konto` is
                    only rendered when integrations are allowlisted for this
                    tenant. Grid column count adapts. */}
                <div
                  className={`grid grid-cols-1 gap-2 ${integrationsAllowed ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
                  data-testid="invoice-primary-actions"
                >
                  <button
                    onClick={() => copySummaryToClipboard()}
                    disabled={saving || sending || invariantBlocked}
                    className="btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-40"
                    data-testid="invoice-copy-summary-btn"
                    title={invariantBlocked
                      ? 'Tölurnar á verkinu stemma ekki, svo samantektin er ekki rétt'
                      : 'Afrita samantekt til að líma í Konto / Payday / DK / bókhaldsforrit'}
                  >
                    <Copy size={16} /> Afrita samantekt
                  </button>
                  {integrationsAllowed && (
                    <button
                      onClick={() => { setError(null); const v = validate(); if (v) { setError(v); return; } setConfirmSend(true); }}
                      disabled={saving || sending || invariantBlocked}
                      className="btn btn-primary flex items-center justify-center gap-2"
                      data-testid="invoice-send-btn"
                      title="Sendir Konto reikning + kröfu til viðskiptavinar"
                    >
                      <Send size={16} /> Senda með Konto
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      setError(null);
                      const v = validate(); if (v) { setError(v); return; }
                      if (!await confirmDialog({
                        title: 'Merkja sem reikningsfært',
                        message: T.manualConfirm,
                        confirmLabel: 'Merkja sem reikningsfært',
                      })) return;
                      await sendNow({ forceManual: true });
                    }}
                    disabled={saving || sending || invariantBlocked}
                    className="btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-40"
                    data-testid="invoice-mark-manual-btn"
                    title={invariantBlocked
                      ? 'Tölurnar á verkinu stemma ekki, Rektra lagar verkið fyrst'
                      : T.manualBtnTitle}
                  >
                    <CheckCircle2 size={16} /> Merkja sem reikningsfært
                  </button>
                </div>

                {/* Fleiri aðgerðir — collapsed secondary exports. PDF + CSV
                    stay accessible for accountants / downstream workflows
                    that need file artefacts; kept quiet by default so the
                    primary flow reads as three equal outcomes. Only reachable
                    once the draft has been persisted (persistedId non-empty). */}
                {persistedId && (
                  <div data-testid="invoice-more-actions">
                    <button
                      type="button"
                      onClick={() => setMoreOpen((v) => !v)}
                      className="text-xs text-foreground-muted hover:text-accent transition-colors inline-flex items-center gap-1 mt-1"
                      data-testid="invoice-more-actions-toggle"
                      aria-expanded={moreOpen}
                    >
                      <ChevronDown
                        size={12}
                        className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`}
                      />
                      Fleiri aðgerðir
                    </button>
                    {moreOpen && (
                      <div className="grid grid-cols-2 gap-2 pt-2" data-testid="invoice-more-actions-panel">
                        <button
                          onClick={() => downloadSummaryPdf()}
                          disabled={saving || sending || exporting === 'pdf'}
                          className="btn btn-tertiary text-xs flex items-center justify-center gap-1.5"
                          data-testid="invoice-download-pdf-btn"
                          title="Sækja PDF samantekt (vinna, efni, upphæð)"
                        >
                          {exporting === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                          Sækja PDF
                        </button>
                        <button
                          onClick={() => downloadSummaryCsv()}
                          disabled={saving || sending || exporting === 'csv'}
                          className="btn btn-tertiary text-xs flex items-center justify-center gap-1.5"
                          data-testid="invoice-download-csv-btn"
                          title="Sækja CSV með línum til að flytja í bókhaldskerfi"
                        >
                          {exporting === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                          Sækja CSV
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </footer>
        )}
      </div>
    </PortalOverlay>
  );
};

export default InvoiceEditorModal;
